import {
  HEAD_FRACTION,
  MAX_SHOT_DISTANCE,
  PLAYER_RADIUS,
  Rng,
  damageAtDistance,
  directionFromAngles,
  vec3,
  type PlayerId,
  type PlayerState,
  type Vec3,
  type WeaponDef,
} from '@game/shared';
import {
  aabbFromCenterHalf,
  rayAabb,
  type Aabb,
  type CollisionWorld,
} from './collision.js';
import { eyePosition, playerHeight } from './movement.js';

/** A player's hitbox at a given moment — used live and for lag-compensated rewind. */
export interface PlayerHitbox {
  id: PlayerId;
  team: number;
  body: Aabb;
  head: Aabb;
}

export const hitboxFor = (
  id: PlayerId,
  team: number,
  position: Vec3,
  height: number,
): PlayerHitbox => {
  const headHeight = height * HEAD_FRACTION;
  return {
    id,
    team,
    body: aabbFromCenterHalf(
      position.x,
      position.y + height / 2,
      position.z,
      PLAYER_RADIUS,
      height / 2,
      PLAYER_RADIUS,
    ),
    head: aabbFromCenterHalf(
      position.x,
      position.y + height - headHeight / 2,
      position.z,
      PLAYER_RADIUS * 0.7,
      headHeight / 2,
      PLAYER_RADIUS * 0.7,
    ),
  };
};

export const hitboxForPlayer = (player: PlayerState): PlayerHitbox =>
  hitboxFor(player.id, player.team, player.position, playerHeight(player));

export interface TraceResult {
  /** Point where the trace stopped, for tracer/impact effects. */
  end: Vec3;
  victim: PlayerId | null;
  headshot: boolean;
  distance: number;
}

/**
 * Single hitscan trace against the static world and a set of player hitboxes.
 * `hitboxes` should already exclude the shooter (and teammates, if friendly fire
 * is off) — this function does not know about game rules.
 */
export const traceShot = (
  world: CollisionWorld,
  origin: Vec3,
  direction: Vec3,
  hitboxes: readonly PlayerHitbox[],
  maxDistance = MAX_SHOT_DISTANCE,
): TraceResult => {
  const worldHit = world.raycast(origin, direction, maxDistance);
  let closest = worldHit ? worldHit.distance : maxDistance;

  const invDx = 1 / (direction.x === 0 ? 1e-12 : direction.x);
  const invDy = 1 / (direction.y === 0 ? 1e-12 : direction.y);
  const invDz = 1 / (direction.z === 0 ? 1e-12 : direction.z);

  let victim: PlayerId | null = null;
  let headshot = false;

  for (const hitbox of hitboxes) {
    const bodyHit = rayAabb(
      origin.x,
      origin.y,
      origin.z,
      invDx,
      invDy,
      invDz,
      hitbox.body,
      closest,
    );
    if (!bodyHit || bodyHit.distance >= closest) continue;
    closest = bodyHit.distance;
    victim = hitbox.id;
    const headHit = rayAabb(
      origin.x,
      origin.y,
      origin.z,
      invDx,
      invDy,
      invDz,
      hitbox.head,
      closest + 0.001,
    );
    headshot = headHit !== null;
  }

  return {
    end: vec3(
      origin.x + direction.x * closest,
      origin.y + direction.y * closest,
      origin.z + direction.z * closest,
    ),
    victim,
    headshot,
    distance: closest,
  };
};

/**
 * Applies a weapon's spread cone to an aim direction. Uses the seeded RNG so the
 * client's predicted tracer matches the server's authoritative trace.
 */
export const applySpread = (
  out: Vec3,
  yaw: number,
  pitch: number,
  spread: number,
  rng: Rng,
): Vec3 => {
  if (spread <= 0) return directionFromAngles(out, yaw, pitch);
  const angle = rng.range(0, Math.PI * 2);
  // sqrt keeps the distribution uniform across the cone's area.
  const radius = Math.sqrt(rng.next()) * spread;
  return directionFromAngles(
    out,
    yaw + Math.cos(angle) * radius,
    pitch + Math.sin(angle) * radius,
  );
};

/** Damage a pellet deals, before armour or other modifiers. */
export const shotDamage = (
  def: WeaponDef,
  distance: number,
  headshot: boolean,
): number => {
  const base = damageAtDistance(def, distance);
  return headshot ? base * def.headshotMultiplier : base;
};

export { eyePosition };
